/**
 * DetectionList Component
 *
 * Scrollable list of detected artifacts with confidence visualization.
 * Click to seek, hover to highlight region.
 */

import { CheckCircle } from 'lucide-react';

import { formatDuration } from '@/lib/design-system';
import type { Detection } from '@/api';

interface DetectionListProps {
  detections: Detection[];
  activeDetection: Detection | null;
  getColorForType: (type: string) => string;
  onDetectionClick: (detection: Detection) => void;
  onDetectionHover: (detection: Detection | null) => void;
}

export function DetectionList({
  detections,
  activeDetection,
  getColorForType,
  onDetectionClick,
  onDetectionHover,
}: DetectionListProps) {
  if (detections.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-success/10 p-4 mb-4">
          <CheckCircle className="h-8 w-8 text-success" />
        </div>
        <p className="text-sm font-medium mb-1">No artifacts detected</p>
        <p className="text-xs text-default-500 max-w-[200px]">
          This file passed artifact detection with the current model and threshold.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-1 overflow-y-auto max-h-[300px]">
      {detections.map((detection, index) => {
        const color = getColorForType(detection.artifact_type);
        const isActive = Boolean(
          activeDetection &&
          activeDetection.start_ms === detection.start_ms &&
          activeDetection.end_ms === detection.end_ms &&
          activeDetection.artifact_type === detection.artifact_type
        );

        return (
          <DetectionRow
            key={`${detection.start_ms}-${detection.end_ms}-${index}`}
            detection={detection}
            color={color}
            isActive={isActive}
            onClick={() => onDetectionClick(detection)}
            onMouseEnter={() => onDetectionHover(detection)}
            onMouseLeave={() => onDetectionHover(null)}
          />
        );
      })}
    </div>
  );
}

interface DetectionRowProps {
  detection: Detection;
  color: string;
  isActive: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function DetectionRow({
  detection,
  color,
  isActive,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: DetectionRowProps) {
  const confidencePercent = Math.round(detection.confidence * 100);

  return (
    <button
      type="button"
      className={`
        w-full flex items-center gap-3 p-2 rounded-lg text-left
        transition-colors
        ${isActive ? 'bg-primary/10 ring-1 ring-primary' : 'hover:bg-default-100'}
      `}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Color indicator */}
      <div
        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: color }}
      />

      {/* Artifact type */}
      <span className="text-sm font-medium min-w-[80px]">
        {detection.artifact_type}
      </span>

      {/* Time range */}
      <span className="text-xs text-default-500 font-mono min-w-[90px]">
        {formatDuration(detection.start_ms)} - {formatDuration(detection.end_ms)}
      </span>

      {/* Confidence bar */}
      <div className="flex-1 flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-default-200 overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{
              width: `${confidencePercent}%`,
              backgroundColor: color,
              opacity: 0.7,
            }}
          />
        </div>
        <span className="text-xs text-default-500 font-mono min-w-[32px] text-right">
          {confidencePercent}%
        </span>
      </div>
    </button>
  );
}
